<!--
  Copyright (C) 2022 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
-->

<template>
  <PrimeDialog
    v-model:visible="isVisible"
    :modal="true"
    :header="$t('dashboardAdmin.versionHistory.title')"
    :style="{ width: '600px' }"
  >
    <div v-if="loading" class="loading-container">
      <PrimeProgressSpinner />
    </div>
    <div v-else-if="versions.length === 0" class="empty-state">
      <i class="ri-history-line empty-icon"></i>
      <p>{{ $t("dashboardAdmin.versionHistory.noVersions") }}</p>
    </div>
    <div v-else class="versions-list">
      <p class="versions-description">
        {{ $t("dashboardAdmin.versionHistory.description") }}
      </p>
      <div
        v-for="(version, index) in versions"
        :key="version.id"
        class="version-item"
        :class="{
          'surface-100': selectedVersion?.id !== version.id,
          'bg-primary-100': selectedVersion?.id === version.id,
          'border-2 border-primary': selectedVersion?.id === version.id,
          'current-version': index === 0,
        }"
        @click="selectVersion(version)"
      >
        <div class="version-content">
          <div class="version-info">
            <div class="version-header">
              <span class="version-number">
                {{ $t("dashboardAdmin.versionHistory.version") }}
                {{ version.versionNumber }}
              </span>
              <PrimeBadge
                v-if="index === 0"
                :value="$t('dashboardAdmin.versionHistory.current')"
                severity="success"
              />
            </div>
            <div class="version-date">
              {{ formatDate(version.modifiedAt) }}
            </div>
            <div class="version-author">
              {{ $t("dashboardAdmin.versionHistory.modifiedBy") }}:
              {{ version.modifiedByUser?.displayName || "Unknown" }}
            </div>
          </div>
          <i
            v-if="selectedVersion?.id === version.id"
            class="ri-check-line selected-check"
          ></i>
        </div>
      </div>
    </div>

    <template #footer>
      <PrimeButton
        :label="$t('cancel')"
        icon="ri-close-line"
        text
        @click="closeDialog"
      />
      <PrimeButton
        :label="$t('dashboardAdmin.versionHistory.revert')"
        icon="ri-history-line"
        :disabled="!selectedVersion || versions.indexOf(selectedVersion) === 0"
        @click="confirmRevert"
      />
    </template>
  </PrimeDialog>
</template>

<script>
import Dialog from "primevue/dialog";
import Button from "primevue/button";
import Badge from "primevue/badge";
import ProgressSpinner from "primevue/progressspinner";
import DashboardService from "@/services/DashboardService.js";

export default {
  name: "VersionHistoryDialog",
  components: {
    PrimeDialog: Dialog,
    PrimeButton: Button,
    PrimeBadge: Badge,
    PrimeProgressSpinner: ProgressSpinner,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    scope: {
      type: String,
      default: "user",
    },
  },
  emits: ["update:visible", "revert"],
  data() {
    return {
      versions: [],
      selectedVersion: null,
      loading: false,
    };
  },
  computed: {
    isVisible: {
      get() {
        return this.visible;
      },
      set(value) {
        this.$emit("update:visible", value);
      },
    },
  },
  watch: {
    visible(newValue) {
      if (newValue) {
        // Set loading state immediately to avoid UI flicker
        this.loading = true;
        this.loadVersionHistory();
      } else {
        this.selectedVersion = null;
      }
    },
  },
  methods: {
    async loadVersionHistory() {
      try {
        this.versions = await DashboardService.getDashboardVersionHistory(
          this.scope,
        );
      } catch (error) {
        console.error("Error loading version history:", error);
        this.$toast.add({
          severity: "error",
          summary: this.$t("error"),
          detail: this.$t("dashboardAdmin.versionHistory.loadError"),
          life: 3000,
        });
      } finally {
        this.loading = false;
      }
    },
    selectVersion(version) {
      this.selectedVersion = version;
    },
    confirmRevert() {
      if (!this.selectedVersion) {
        return;
      }
      this.$emit("revert", this.selectedVersion);
      this.closeDialog();
    },
    closeDialog() {
      this.isVisible = false;
    },
    formatDate(dateString) {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat(this.$i18n.locale, {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }).format(date);
    },
  },
};
</script>

<style scoped>
.loading-container {
  display: flex;
  justify-content: center;
  padding: 1rem;
}

.empty-state {
  text-align: center;
  padding: 1rem;
  color: var(--p-text-muted-color);
}

.empty-icon {
  font-size: 2rem;
  margin-bottom: 0.5rem;
}

.versions-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.versions-description {
  font-size: 0.875rem;
  color: var(--p-text-muted-color);
  margin-bottom: 0.5rem;
}

.version-item {
  padding: 0.75rem;
  border-radius: var(--p-border-radius);
  cursor: pointer;
  transition: all 0.2s;
  border: 2px solid transparent;
}

.version-item:hover {
  background: var(--p-surface-200);
}

.version-item.current-version {
  border-left: 4px solid var(--p-green-500);
}

.version-content {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
}

.version-info {
  flex: 1;
}

.version-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.25rem;
}

.version-number {
  font-weight: 600;
}

.version-date {
  font-size: 0.875rem;
  color: var(--p-text-muted-color);
}

.version-author {
  font-size: 0.875rem;
  color: var(--p-text-muted-color);
}

.selected-check {
  color: var(--p-primary-color);
  font-size: 1.25rem;
}
</style>
